import { T } from "../../../i18n/social";
import React, { useEffect, useState } from "react";

const StepSuccess = ({ formData, selectedDate, selectedSlot, consultationType, bookingRef, outlookLink, lang }) => {
  const tsc = lang && T[lang]?.steps?.success ? T[lang].steps.success : T['de'].steps.success;
  const [animate, setAnimate] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setAnimate(true), 100);
    const t2 = setTimeout(() => setShowDetails(true), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const dateLabel = selectedDate
    ? selectedDate?.toLocaleDateString(lang === "uk" ? "uk-UA" : lang === "ru" ? "ru-RU" : "de-DE", {
        weekday: "long", day: "2-digit", month: "2-digit", year: "numeric",
      })
    : "";

  const typeLabel = consultationType === "onsite" ? tsc.onsiteLabel : tsc.phoneLabel;

  return (
    <div className="booking-card step-enter" style={{ textAlign: 'center' }}>
      {/* Green free badge */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 16px',
        background: 'rgba(22, 163, 74, 0.08)',
        border: '1px solid rgba(22, 163, 74, 0.25)',
        borderRadius: '100px',
        marginBottom: '20px',
        opacity: animate ? 1 : 0,
        transition: 'opacity 400ms ease 100ms',
      }}>
        <span style={{ fontSize: '14px', color: '#16a34a', fontWeight: 700, fontFamily: 'var(--font-ui)' }}>{tsc.badge}</span>
      </div>

      {/* Animated success circle */}
      <div style={{
        margin: '0 auto 24px',
        width: '88px',
        height: '88px',
        borderRadius: '50%',
        background: 'rgba(22, 163, 74, 0.08)',
        border: '2px solid rgba(22, 163, 74, 0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 32px rgba(22, 163, 74, 0.12)',
        transform: animate ? 'scale(1)' : 'scale(0)',
        transition: 'transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
          <path
            d="M10 22 L19 31 L34 14"
            stroke="#16a34a"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            style={{ strokeDasharray: 36, strokeDashoffset: animate ? 0 : 36, transition: 'stroke-dashoffset 400ms ease-out 200ms' }}
          />
        </svg>
      </div>

      <h2 style={{
        fontFamily: 'var(--font-heading)',
        fontSize: '30px',
        fontWeight: 700,
        color: '#1e2b38',
        marginBottom: '8px',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        opacity: animate ? 1 : 0,
        transform: animate ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 400ms ease 300ms, transform 400ms ease 300ms',
      }}>
        {tsc.title || "Termin bestätigt! 🎉"}
      </h2>
      <p style={{
        fontSize: '15px',
        color: '#6b7a8d',
        fontFamily: 'var(--font-ui)',
        marginBottom: '28px',
        lineHeight: 1.6,
        opacity: animate ? 1 : 0,
        transition: 'opacity 400ms ease 400ms',
      }}>
        Vielen Dank, <strong style={{ color: '#1e2b38' }}>{formData?.vorname}</strong>! {tsc.sub || "Wir freuen uns auf das Gespräch."}
      </p>

      {/* Appointment Details */}
      <div style={{
        background: '#f8f9fa',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '16px',
        textAlign: 'left',
        border: '1px solid #dde1e7',
        opacity: showDetails ? 1 : 0,
        transform: showDetails ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 400ms ease, transform 400ms ease',
      }}>
        <p style={{ fontSize: '11px', fontWeight: 700, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px', fontFamily: 'var(--font-ui)' }}>
          {tsc.detailsTitle || 'Deine Termindetails'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { icon: '🏷️', text: typeLabel },
            { icon: '📅', text: <span style={{ textTransform: 'capitalize' }}>{dateLabel}</span> },
            { icon: '🕐', text: tsc.uhr ? `${selectedSlot} ${tsc.uhr}` : selectedSlot },
            consultationType === 'onsite'
              ? { icon: '📍', text: 'Kruppstraße 41-45, 42489 Wülfrath' }
              : { icon: '📞', text: lang === 'inst' ? `Wir rufen Sie unter ${formData?.telefon} an.` : `Wir rufen dich unter +49 ${formData?.telefon} an.` },
          ]?.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ fontSize: '14px', flexShrink: 0 }}>{item?.icon}</span>
              <span style={{ fontSize: '14px', color: '#1e2b38', fontFamily: 'var(--font-ui)', lineHeight: 1.5 }}>{item?.text}</span>
            </div>
          ))}

          {/* Booking Reference */}
          {bookingRef && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', paddingTop: '10px', borderTop: '1px solid #dde1e7', marginTop: '2px' }}>
              <span style={{ fontSize: '14px', flexShrink: 0 }}>🔖</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-ui)' }}>{tsc.ref || 'BUCHUNGSREFERENZ'}</span>
                <span style={{ fontSize: '14px', color: '#1e2b38', fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.04em' }}>{bookingRef}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email notice */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '14px 16px',
        background: '#f1f3f5',
        border: '1px solid #dde1e7',
        borderRadius: '14px',
        marginBottom: '16px',
        textAlign: 'left',
        opacity: showDetails ? 1 : 0,
        transition: 'opacity 400ms ease 100ms',
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7a8d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
        <p style={{ fontSize: '13px', color: '#6b7a8d', fontFamily: 'var(--font-ui)', lineHeight: 1.5 }}>
          Eine Bestätigungs-E-Mail wurde an <strong style={{ color: '#1e2b38' }}>{formData?.email}</strong> gesendet.
        </p>
      </div>

      {/* Outlook Calendar Link */}
      {outlookLink && (
        <a
          href={outlookLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            padding: '13px 20px',
            marginBottom: '12px',
            background: '#fff',
            border: '1.5px solid #3E4954',
            borderRadius: '12px',
            color: '#3E4954',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: 'var(--font-ui)',
            textDecoration: 'none',
            cursor: 'pointer',
            opacity: showDetails ? 1 : 0,
            transition: 'opacity 400ms ease 200ms, background 200ms',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f0f2f4'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
        >
          {/* Outlook calendar icon */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3E4954" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          In Outlook-Kalender öffnen
        </a>
      )}

      <button
        className="btn-primary"
        style={{ width: '100%' }}
        onClick={() => window.open("https://zba.academy", "_blank")}
      >
        Zur ZBA Academy Website
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          <polyline points="15 3 21 3 21 9"/>
          <line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
      </button>
    </div>
  );
};

export default StepSuccess;